import {EventBatchSource} from "@measure-up/rhythm";
import {PhraseCycle} from "./phrase-cycle";
import {mergeBar, PhraserEvent} from "./phraser-events";

interface PhraserEventSourceConfig {
    beatsPerBar: number;
    /** When false, the click field is omitted from every event (beat onsets remain, as silent carriers). */
    hasClickTrack: boolean;
}

/**
 * The melody/click event source for Phraser's scheduler: one call = one
 * bar, merged from the click track and PhraseCycle's next batch.
 *
 * beatsPerBar and hasClickTrack are live-read from the config on every
 * call (the repo's established live-read pattern). Silencing clicks omits
 * the click FIELD only - because the merged stream tiles time with
 * durations regardless of what sounds, silencing never affects timing.
 *
 * A factory function, not a class: it is stateless itself - all cycle
 * state lives in PhraseCycle.
 */
function createPhraserEventSource(
    config: PhraserEventSourceConfig,
    phraseCycle: PhraseCycle,
): EventBatchSource<PhraserEvent> {
    return () => {
        const events = mergeBar(config.beatsPerBar, phraseCycle.nextBatch());
        if (config.hasClickTrack) {
            return events;
        }
        return events.map(event => {
            const silenced = {...event};
            delete silenced.click;
            return silenced;
        });
    };
}

export {createPhraserEventSource, PhraserEventSourceConfig};
